import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { eventDateFormatter, fourDigitNumberFormatter, percentFormatter } from "../formatters";
import { GridItem, PlayerWithLinkColumn, overrideColumn } from "../common-columns";

export interface BattingStandardFormattedRow {
  player: {
    id: string;
    teamID: string;
    name: string;
    status: string;
  };
  PA: number;
  AB: number;
  AVG: number;
  OBP: number;
  OPS: number;
  SLG: number;
  H: number;
  ["1B"]: number;
  ["2B"]: number;
  ["3B"]: number;
  HR: number;
  RBI: number;
  R: number;
  BB: number;
  SO: number;
  ["K-L"]: number;
  HBP: number;
  SAC: number;
  SF: number;
  ROE: number;
  FC: number;
  SB: number;
  ["SB%"]: number;
  CS: number;
  PIK: number;
}

export const battingStandardGridDataFormatted = (grid: GridItem[]): BattingStandardFormattedRow[] => {
  return grid
    .filter(({ offense }) => offense)
    .map(({ person_id, team_id, name, eventID, date, offense }) => {
      return {
        player: {
          id: person_id,
          teamID: team_id,
          name,
          eventID,
          status: "active",
        },
        // Read by the per-game Date column; undefined (and ignored) in season/box-score views.
        event: date,
        GP: offense.gp,
        PA: offense.pa,
        AB: offense.ab,
        AVG: offense.avg,
        OBP: offense.obp,
        OPS: offense.ops,
        SLG: offense.slg,
        H: offense.h,
        // eslint-disable-next-line
        ["1B"]: offense["1B"],
        // eslint-disable-next-line
        ["2B"]: offense["2B"],
        // eslint-disable-next-line
        ["3B"]: offense["3B"],
        HR: offense.hr,
        RBI: offense.rbi,
        R: offense.r,
        BB: offense.bb,
        SO: offense.so,
        // eslint-disable-next-line
        ["K-L"]: offense.sol,
        HBP: offense.hbp,
        SAC: offense.shb,
        SF: offense.shf,
        ROE: offense.roe,
        FC: offense.fc,
        SB: offense.sb,
        // eslint-disable-next-line
        ["SB%"]: offense["SB%"],
        CS: offense.cs,
        PIK: offense.pik,
      };
    });
};

const CoreBattingStandard: StatsInfo[] = [
  { name: "PA", field: "PA", def: "Plate appearances" },
  { name: "AB", field: "AB", def: "At bats" },
  {
    name: "AVG",
    field: "AVG",
    def: "Batting average",
    valueFormatter: fourDigitNumberFormatter,
    sort: "desc",
  },
  {
    name: "OBP",
    field: "OBP",
    def: "On-base percentage",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "OPS",
    field: "OPS",
    def: "On-base percentage plus slugging percentage",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "SLG",
    field: "SLG",
    def: "Slugging percentage",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "H", field: "H", def: "Hits" },
  { name: "1B", field: "1B", def: "Singles" },
  { name: "2B", field: "2B", def: "Doubles" },
  { name: "3B", field: "3B", def: "Triples" },
  { name: "HR", field: "HR", def: "Home runs" },
  { name: "RBI", field: "RBI", def: "Runs batted in" },
  { name: "R", field: "R", def: "Runs scored" },
  { name: "BB", field: "BB", def: "Base on balls (walks)" },
  { name: "SO", field: "SO", def: "Strikeouts" },
  { name: "K-L", field: "K-L", def: "Strikeouts looking" },
  { name: "HBP", field: "HBP", def: "Hit by pitch" },
  { name: "SAC", field: "SAC", def: "Sacrifice hits & bunts" },
  { name: "SF", field: "SF", def: "Sacrifice flies" },
  { name: "ROE", field: "ROE", def: "Reached on error" },
  { name: "FC", field: "FC", def: "Hit into fielder's choice" },
  { name: "SB", field: "SB", def: "Stolen bases" },
  { name: "SB%", field: "SB%", def: "Stolen base percentage", valueFormatter: percentFormatter },
  { name: "CS", field: "CS", def: "Caught stealing" },
  { name: "PIK", field: "PIK", def: "Picked off" },
];

export const BattingStandard: StatsInfo[] = (
  [PlayerWithLinkColumn, { name: "GP", field: "GP", def: "Games played" }] as StatsInfo[]
).concat(CoreBattingStandard);

export const EventBattingStandard: StatsInfo[] = (
  [
    {
      name: "Date",
      field: "event",
      columnProps: {
        width: 100,
        pinned: true,
        cellStyle: { textAlign: "left" },
        sort: "asc",
        valueFormatter: eventDateFormatter,
      },
    },
    {
      name: "OPPONENT",
      field: "opponent",
      columnProps: {
        width: 260,
        cellStyle: { textAlign: "left" },
        headerClass: "left-aligned-column-header",
      },
    },
  ] as StatsInfo[]
).concat(CoreBattingStandard);

// Event view shows AVG without the default desc sort the season view uses.
overrideColumn(EventBattingStandard, "AVG", {
  name: "AVG",
  field: "AVG",
  def: "Batting average",
  valueFormatter: fourDigitNumberFormatter,
});

const excludedStats = ["AVG", "OBP", "OPS", "SLG", "SB%"];

export const GameBattingStandard: StatsInfo[] = ([PlayerWithLinkColumn] as StatsInfo[]).concat(
  [...CoreBattingStandard].filter((stat) => !excludedStats.includes(stat.name))
);

overrideColumn(GameBattingStandard, "H", { name: "H", field: "H", def: "Hits", columnProps: { sort: "desc" } });
